<template>
  <loading-spinner v-if="isLoading" medium class="pb-5"></loading-spinner>
  <v-container v-else fluid class="ma-0 pa-0">
    <v-expansion-panels v-model="openedFilterPanel" default class="py-2 px-1">
      <v-expansion-panel>
        <v-expansion-panel-header
          class="text-h6"
          style="background-color: #e0e0e9; color: black;"
        >
          <v-tooltip top color="primary" class="pl-4">
            <template v-slot:activator="{ on: tooltip }">
              <div v-on="{ ...tooltip }">
                Filter datasets
                <v-chip
                  label
                  :color="
                    getVisibleDatasetCount() < datasets.length
                      ? colors['population']
                      : 'primary'
                  "
                  :text-color="
                    getVisibleDatasetCount() < datasets.length
                      ? 'black'
                      : 'white'
                  "
                  class="title ml-3"
                >
                  {{ getVisibleDatasetCount() }} /
                  {{ datasets.length }} datasets shown
                </v-chip>
                <v-chip
                  label
                  :color="
                    selectedProjects.length < getDatasetProjects().length
                      ? colors['population']
                      : 'primary'
                  "
                  text-color="selectedProjects.length < getDatasetProjects().length ? 'black' : 'white'"
                  class="title ml-3"
                >
                  {{ selectedProjects.length }} /
                  {{ getDatasetProjects().length }} projects selected
                </v-chip>
                <v-spacer></v-spacer>
              </div>
            </template>
            <span class="subtitle-1">
              {{
                openedFilterPanel == 0
                  ? 'click to hide dataset filters'
                  : 'click to view dataset filters'
              }}
            </span>
          </v-tooltip>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="pl-1">
          <v-container class="pa-0 ma-0 pt-2" fluid>
            <v-row class="pa-0 ma-0">
              <v-col cols="12" class="pa-0 ma-0 pt-2">
                Filter by project:<br clear="both" />

                <v-data-table
                  ref="project_table"
                  v-model="selectedProjects"
                  show-select
                  multi-sort
                  :headers="projectHeaders"
                  :items="getDatasetProjects()"
                  item-key="id"
                  disable-pagination
                  hide-default-footer
                  dense
                >
                </v-data-table>
              </v-col>
            </v-row>
          </v-container>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>

    <v-data-table
      ref="dataset_table"
      v-model="selected"
      :sort-by="['project_name']"
      show-select
      multi-sort
      :search="search"
      :headers="headers"
      :items="datasets"
      item-key="study_name"
      class="pb-1"
      disable-pagination
      hide-default-footer
    >
      <!-- Add "All:" to select all checkbox -->
      <template v-slot:header.data-table-select="{ on, props }">
        <v-tooltip top color="primary">
          <template v-slot:activator="{ on: tooltip }">
            <span
              class="text-subtitle-1 font-weight-bold"
              v-on="{ ...tooltip }"
            >
              All: <v-simple-checkbox v-bind="props" v-on="on"
            /></span>
          </template>
          <span class="subtitle-1">Click to select all datasets.</span>
        </v-tooltip>
      </template>

      <template v-slot:item.project_name="{ item }">
        <td class="subtitle-1 text-xs-left">{{ item.project_name }}</td>
      </template>

      <template v-slot:item.study_name="{ item }">
        <td class="subtitle-1 text-xs-left">{{ item.study_name }}</td>
      </template>

      <template v-slot:item.longitudinal="{ item }">
        <td class="text-subtitle-1 text-xs-center">
          <v-tooltip v-if="item.longitudinal" top color="primary">
            <template v-slot:activator="{ on: tooltip }">
              <span v-on="{ ...tooltip }">
                <v-chip
                  label
                  class="font-weight-bold px-2"
                  color="green lighten-4"
                  ><span class="ma-0 pa-0" style="width: 0.4em;"></span>L<span
                    class="ma-0 pa-0"
                    style="width: 0.4em;"
                  ></span
                  ><v-icon v-if="false" class="pl-2"
                    >mdi-chart-timeline-variant</v-icon
                  ></v-chip
                >
              </span>
            </template>
            <span class="subtitle-1">Longitudinal</span>
          </v-tooltip>

          <v-tooltip v-else top color="primary">
            <template v-slot:activator="{ on: tooltip }">
              <v-chip
                label
                class="font-weight-bold px-2"
                color="blue lighten-4"
                v-on="{ ...tooltip }"
                >CS<v-icon v-if="false" class="pl-2"
                  >mdi-drag-vertical-variant</v-icon
                ></v-chip
              >
            </template>
            <span class="subtitle-1">Cross-sectional</span>
          </v-tooltip>
        </td>
      </template>

      <template v-slot:item.is_public="{ item }">
        <td class="text-subtitle-1 text-xs-center">
          <v-tooltip v-if="item.is_public" top color="primary">
            <template v-slot:activator="{ on: tooltip }">
              <span v-on="{ ...tooltip }">
                <v-chip
                  label
                  class="font-weight-bold pa-2 mr-2"
                  color="green lighten-4"
                >
                  <v-icon center>mdi-lock-open-variant-outline</v-icon>
                </v-chip>
              </span>
            </template>
            <span class="subtitle-1"> Public </span>
          </v-tooltip>

          <v-tooltip v-else top color="primary">
            <template v-slot:activator="{ on: tooltip }">
              <span v-on="{ ...tooltip }">
                <v-chip
                  label
                  class="font-weight-bold pa-2 mr-2"
                  color="red lighten-4"
                >
                  <v-icon center>mdi-lock-outline</v-icon>
                </v-chip>
              </span>
            </template>
            <span class="subtitle-1"> Private </span>
          </v-tooltip>
        </td>
      </template>

      <template v-slot:item.num_subjects="{ item }">
        <td class="subtitle-1 text-xs-left">{{ item.num_subjects }}</td>
      </template>

      <template v-slot:item.name="{ item }">
        <td class="text-subtitle-1 text-xs-left">
          <v-tooltip top color="primary">
            <template v-slot:activator="{ on: tooltip }">
              <v-icon
                color="primary"
                class="mr-1"
                @click="openDatasetSummary(item.id)"
                v-on="{ ...tooltip }"
                >info</v-icon
              >
            </template>
            <span class="subtitle-1">Learn more about this dataset.</span>
          </v-tooltip>
        </td>
      </template>
      <template v-slot:no-data>
        No datasets available.
      </template>
    </v-data-table>

    <v-dialog v-model="dialog" width="60%" :fullscreen="fullscreen" scrollable>
      <v-card
        class="ma-0 pa-0 indigo lighten-5"
        :height="fullscreen ? '100vh' : '75vh'"
      >
        <v-card-title class="ma-0 pa-0">
          <v-toolbar class="primary">
            <v-icon color="white" large>mdi-layers-search</v-icon>
            <v-toolbar-title class="white--text pl-3"
              >Dataset Summary
            </v-toolbar-title>
            <v-spacer></v-spacer>
            <v-tooltip bottom color="primary">
              <template v-slot:activator="{ on: tooltip }">
                <v-btn
                  icon
                  class="white--text ma-0 pa-0 mr-2"
                  @click="fullscreen = !fullscreen"
                  v-on="{ ...tooltip }"
                >
                  <v-icon center>{{
                    fullscreen ? 'mdi-arrow-collapse' : 'mdi-arrow-expand'
                  }}</v-icon>
                </v-btn>
              </template>
              <span class="subtitle-1">{{
                fullscreen ? 'Exit fullscreen' : 'Expand to fullscreen'
              }}</span>
            </v-tooltip>
            <v-tooltip bottom color="primary">
              <template v-slot:activator="{ on: tooltip }">
                <v-btn
                  icon
                  class="white--text ma-0 pa-0"
                  @click="dialog = false"
                  v-on="{ ...tooltip }"
                >
                  <v-icon center>close</v-icon>
                </v-btn>
              </template>
              <span class="subtitle-1">Close</span>
            </v-tooltip>
          </v-toolbar>
        </v-card-title>

        <v-card-text class="ma-0 pa-0">
          <dataset-summary :id="clickedId" />
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import { state, actions } from '@/store/modules/datasetManager/types';
import { state as authState } from '@/store/modules/auth/types';

import DatasetSummary from '@/views/DatasetSummary.vue';
import { colors } from '@/utils/colors';

// localStorage key for visible projects list
const VP_KEY_PREFIX = 'visible_projects';
const VP_DELIM = ',';

export default {
  components: {
    DatasetSummary,
  },
  props: {
    search: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      selected: [],
      dialog: false,
      fullscreen: false,
      clickedId: 0,
      openedFilterPanel: null,
      colors: colors,
      headers: [
        {
          text: 'Project',
          value: 'project_name',
          class: 'text-subtitle-1 font-weight-bold',
          sortable: true,
          filter: (value, search, item) => {
            return item.project_id in this.selectedProjectIdsH;
          },
        },
        {
          text: 'Dataset',
          value: 'study_name',
          class: 'text-subtitle-1 font-weight-bold',
          sortable: true,
        },
        {
          text: 'Type',
          value: 'longitudinal',
          class: 'text-subtitle-1 font-weight-bold',
          sortable: true,
        },
        {
          text: 'Access',
          value: 'is_public',
          class: 'text-subtitle-1 font-weight-bold',
          sortable: true,
        },
        {
          text: 'Info',
          value: 'name',
          align: 'left',
          sortable: false,
          class: 'text-subtitle-1 font-weight-bold',
        },
        {
          text: 'Subject Count',
          value: 'num_subjects',
          class: 'text-subtitle-1 font-weight-bold',
          sortable: true,
        },
      ],
      projectHeaders: [
        {
          text: 'Project',
          value: 'name',
          class: 'text-subtitle-1 font-weight-bold',
          sortable: true,
        },
        {
          text: 'Primary Disease',
          value: 'primary_disease',
          class: 'text-subtitle-1 font-weight-bold',
          sortable: true,
        },
      ],
      selectedProjects: [],
    };
  },
  computed: {
    ...mapState('auth', {
      user: authState.USER,
    }),
    ...mapState('datasetManager', {
      isLoading: state.IS_LOADING,
      datasets: state.DATASETS,
      selectedDatasets: state.SELECTED_DATASETS,
    }),
    selectedProjectIdsH() {
      let pid_h = {};
      this.selectedProjects.forEach(sp => {
        pid_h[sp.id] = 1;
      });
      return pid_h;
    },
    vpKey() {
      return VP_KEY_PREFIX + '-' + this.user.id;
    },
  },
  watch: {
    selected() {
      this.selectDatasets(this.selected);
    },
    selectedProjects(sp) {
      // save selected projects in local storage
      let sps = window.localStorage.getItem(this.vpKey);
      let new_sps = sp.map(p => p.id).join(VP_DELIM);
      if (new_sps != sps) {
        window.localStorage.setItem(this.vpKey, new_sps);
      }
    },
  },
  async mounted() {
    await this.fetchDatasets();
    this.selected = [];
    let dps = this.getDatasetProjects();
    // filter projects based on user preference stored in localStorage
    let sps = window.localStorage.getItem(this.vpKey);
    let sph = {};
    if (sps != null) {
      sps.split(VP_DELIM).map(pid => {
        sph[pid] = 1;
      });
      dps = dps.filter(dp => dp.id in sph);
    }
    this.selectedProjects = dps;
  },
  methods: {
    ...mapActions('datasetManager', {
      fetchDatasets: actions.FETCH_DATASETS,
      selectDatasets: actions.SELECT_DATASETS,
    }),
    openDatasetSummary(id) {
      this.clickedId = id;
      this.dialog = true;
    },
    getVisibleDatasetCount() {
      if (!('dataset_table' in this.$refs) || this.$refs.dataset_table == null)
        return 0;
      if (!('internalCurrentItems' in this.$refs.dataset_table)) return 0;
      return this.$refs.dataset_table.internalCurrentItems.length;
    },
    getDatasetProjects() {
      let proj_ids = {};
      let projects = [];
      this.datasets.forEach(d => {
        let project_id = d.project_id;
        let project_name = d.project_name;
        let disease = d.primary_disease;
        if (!(project_id in proj_ids)) {
          proj_ids[project_id] = true;
          projects.push({
            id: project_id,
            name: project_name,
            primary_disease: disease,
          });
        }
      });
      projects.sort((a, b) => a.name.localeCompare(b.name));
      return projects;
    },
  },
};
</script>

<style></style>
